import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authenticate, clearAccessToken, getMe, hasAccessToken } from '../services/api';

export type UserRole = 'superadmin' | 'admin' | 'qualidade' | 'supervisor' | 'operador';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string | null;
  sectorId?: string;
  supervisorId?: string;
  monitorId?: string;
  tenantId?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  canViewAllOperators: boolean;
  canEditScorecard: boolean;
  canUploadAudits: boolean;
  canPerformAudit: boolean;
  canViewQualityDashboard: boolean;
  canManageTenants: boolean;
  isSuperAdmin: boolean;
  getVisibleOperatorIds: () => string[];
}

const AUTH_CONTEXT_FALLBACK: AuthContextType = {
  user: null,
  isAuthLoading: false,
  login: async () => false,
  logout: async () => {},
  updateUser: () => {},
  canViewAllOperators: false,
  canEditScorecard: false,
  canUploadAudits: false,
  canPerformAudit: false,
  canViewQualityDashboard: false,
  canManageTenants: false,
  isSuperAdmin: false,
  getVisibleOperatorIds: () => [],
};

const AuthContext = createContext<AuthContextType>(AUTH_CONTEXT_FALLBACK);

async function loadUserProfile(): Promise<User | null> {
  const me = await getMe<User>();
  if (!me) return null;
  return me;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      setIsAuthLoading(true);

      if (!hasAccessToken()) {
        if (isMounted) {
          setUser(null);
          setIsAuthLoading(false);
        }
        return;
      }

      const profile = await loadUserProfile();
      if (!isMounted) return;

      if (!profile) {
        clearAccessToken();
        setUser(null);
      } else {
        setUser(profile);
      }
      setIsAuthLoading(false);
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const auth = await authenticate(email, password);
    if (!auth) {
      throw new Error('E-mail ou senha incorretos.');
    }

    const profile = auth.user || await loadUserProfile();
    if (!profile) {
      clearAccessToken();
      throw new Error('Usuario autenticado sem perfil de permissao vinculado.');
    }

    setUser(profile);
    return true;
  };

  const logout = async () => {
    clearAccessToken();
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const permissions = useMemo(() => {
    const isSuperAdmin = user?.role === 'superadmin';
    const canViewAllOperators = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'qualidade';
    const canEditScorecard = user?.role === 'superadmin' || user?.role === 'admin';
    const canUploadAudits = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'qualidade';
    const canPerformAudit = canUploadAudits || user?.role === 'supervisor';
    const canViewQualityDashboard = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'qualidade';
    const canManageTenants = user?.role === 'superadmin';
    return {
      isSuperAdmin: Boolean(isSuperAdmin),
      canViewAllOperators: Boolean(canViewAllOperators),
      canEditScorecard: Boolean(canEditScorecard),
      canUploadAudits: Boolean(canUploadAudits),
      canPerformAudit: Boolean(canPerformAudit),
      canViewQualityDashboard: Boolean(canViewQualityDashboard),
      canManageTenants: Boolean(canManageTenants),
    };
  }, [user]);

  const getVisibleOperatorIds = (): string[] => {
    if (!user) return [];
    if (user.role === 'operador') return [user.id];
    return [];
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthLoading,
        login,
        logout,
        updateUser,
        ...permissions,
        getVisibleOperatorIds,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}
